import React, { useEffect, useState } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from 'react-hot-toast';

export default function HolidayMasterList() {
    const { http, user } = AuthUser();
    const [holidayMasters, setHolidayMasters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        number_leave: ''
    });

    useEffect(() => {
        fetchHolidayMasters();
    }, []);

    const fetchHolidayMasters = async () => {
        setLoading(true);
        try {
            const response = await http.get(`/holiday_masters?login_user=${user.id}`);
            if (response.data.success) {
                setHolidayMasters(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching holiday masters:', error);
            toast.error('Error fetching holiday masters');
        } finally {
            setLoading(false);
        }
    };

    const openAddModal = () => {
        setEditingItem(null);
        setFormData({ name: '', number_leave: '' });
        setShowModal(true);
    };

    const openEditModal = (item) => {
        setEditingItem(item);
        setFormData({
            name: item.name || '',
            number_leave: item.number_leave || ''
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingItem(null);
        setFormData({ name: '', number_leave: '' });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name) {
            toast.error('Please enter name');
            return;
        }

        setLoading(true);
        try {
            const requestData = {
                ...formData,
                login_user: user.id
            };

            let response;
            if (editingItem) {
                response = await http.put(`/holiday_master/${editingItem.id}`, requestData);
            } else {
                response = await http.post('/holiday_master', requestData);
            }

            if (response.data.success) {
                toast.success(editingItem ? 'Updated successfully!' : 'Created successfully!');
                closeModal();
                fetchHolidayMasters();
            } else {
                toast.error(response.data.message || 'Failed to save');
            }
        } catch (error) {
            console.error('Error saving:', error);
            toast.error(error.response?.data?.message || 'Error saving record');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this holiday type? This will also delete all related holiday dates.')) {
            return;
        }
        
        try {
            const response = await http.delete(`/holiday_master/${id}`);
            if (response.data.success) {
                toast.success('Deleted successfully!');
                fetchHolidayMasters();
            } else {
                toast.error(response.data.message || 'Failed to delete');
            }
        } catch (error) {
            console.error('Error deleting:', error);
            toast.error('Error deleting record');
        }
    };

    // Calculate total leaves allowed
    const totalLeavesAllowed = holidayMasters.reduce((sum, item) => sum + (parseInt(item.number_leave) || 0), 0);

    return (
        <>
            <Toaster />
            <div className="container-fluid mt-4 px-4">
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h3>Holiday Master (Leave Types)</h3>
                            <button className="btn btn-primary" onClick={openAddModal}>
                                <i className="fa fa-plus me-1"></i> Add New
                            </button>
                        </div>

                        {/* Summary Card */}
                        <div className="row mb-4">
                            <div className="col-md-4">
                                <div className="card bg-primary text-white">
                                    <div className="card-body">
                                        <h5 className="card-title">Total Leave Types</h5>
                                        <h2>{holidayMasters.length}</h2>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="card bg-success text-white">
                                    <div className="card-body">
                                        <h5 className="card-title">Total Leaves Allowed</h5>
                                        <h2>{totalLeavesAllowed} Days</h2>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="card">
                            <div className="card-header">
                                <h5 className="mb-0">Leave Types</h5>
                            </div>
                            <div className="card-body">
                                {loading ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : holidayMasters.length === 0 ? (
                                    <div className="text-center py-4">
                                        <p className="text-muted">No holiday types found. Click "Add New" to create one.</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th>Name</th>
                                                    <th>Number of Leaves</th>
                                                    <th>Holiday Dates</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {holidayMasters.map((item, index) => (
                                                    <tr key={item.id}>
                                                        <td>{index + 1}</td>
                                                        <td><strong>{item.name || 'N/A'}</strong></td>
                                                        <td>
                                                            <span className="badge bg-info">
                                                                {item.number_leave || 0} Days
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-warning">
                                                                {item.details?.length || 0} dates
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="btn-group btn-group-sm">
                                                                <button 
                                                                    className="btn btn-outline-primary"
                                                                    onClick={() => openEditModal(item)}
                                                                    title="Edit"
                                                                >
                                                                    <i className="fa fa-edit"></i>
                                                                </button>
                                                                <button
                                                                    className="btn btn-outline-danger"
                                                                    onClick={() => handleDelete(item.id)}
                                                                    title="Delete"
                                                                >
                                                                    <i className="fa fa-trash"></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot className="table-light">
                                                <tr>
                                                    <th colSpan="2">Total</th>
                                                    <th>
                                                        <span className="badge bg-success">
                                                            {totalLeavesAllowed} Days
                                                        </span>
                                                    </th>
                                                    <th colSpan="2"></th>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-primary text-white">
                                <h5 className="modal-title">
                                    {editingItem ? 'Edit' : 'Add'} Holiday Type
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={closeModal}
                                ></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            placeholder="e.g., Casual Leave, Sick Leave"
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Number of Leaves</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            name="number_leave"
                                            value={formData.number_leave}
                                            onChange={handleChange}
                                            placeholder="Enter number of leaves allowed"
                                            min="0"
                                        />
                                        <small className="text-muted">
                                            Number of leaves allowed per year for this type
                                        </small>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={closeModal}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={loading}
                                    >
                                        {loading ? 'Saving...' : (editingItem ? 'Update' : 'Save')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

